/**
 * Zustand store for the image → Figma import flow.
 *
 * State is intentionally fine-grained so components can subscribe to only the
 * slice they render — e.g. high-frequency `progress` updates re-render just the
 * status panel, not the dropzone, mode picker, or header.
 *
 * The ImageMagick Web Worker and the plugin postMessage bridge are managed here as
 * module-level singletons (created once, no React lifecycle), so there's no
 * effect churn and the plugin handshake is wired up the moment the UI loads.
 */

import { create } from 'zustand';
import { conformsToImporterFlow } from '@figma-importers/ui/contract';
import type {
  EditorType,
  ImportMode,
  PluginMessage,
  UIMessage,
} from '../types/messages';
import { PROTOCOL_VERSION } from '../types/messages';
import type { WorkerRequest, WorkerResponse } from '../lib/worker-messages';
import { createLicensing, type Gate, type ImportKind, type LicenseLinkName, type LicenseUser } from '@figma-importers/licensing';
import { validateFile } from '../lib/validate';
import { DEFAULT_VARIANT, variantById, type Variant } from '../variants';


/**
 * The free-tier gate and the licence, shared with every tomake.design importer that
 * charges — map-to-design is free and deliberately has none:
 * five imports a month here, Pro everywhere once a licence is known for the
 * signed-in Figma user (see @figma-importers/licensing).
 */
export const licensing = createLicensing({ app: DEFAULT_VARIANT.key, productName: DEFAULT_VARIANT.name });

/**
 * How the licence server classifies a mode.
 *
 * Only an editable import spends the monthly allowance; placing the page as a picture is free
 * on every plan, so 'image' must not be counted and must never be refused for being the sixth.
 * 'text' is editable — it produces real text layers, which is the thing being paid for.
 */
const kindOf = (mode: ImportMode): ImportKind => (mode === 'image' ? 'image' : 'editable');

export type ImportStatus =
  | 'idle'
  | 'converting'
  | 'placing'
  | 'done'
  | 'error';

export interface ImportProgress {
  completed: number;
  total: number;
}

interface ImportState {
  // Connection
  isReady: boolean;
  editorType: EditorType | null;
  /**
   * Set when the UI↔plugin handshake never completes — the bridge is dead, so
   * the UI shows a recoverable error instead of an indefinite "Connecting…".
   */
  connectionError: string | null;
  /**
   * Set when the plugin handshakes but reports a different (or missing) protocol
   * version — the locally-loaded `plugin/code.js` is out of date relative to
   * this (auto-deployed) UI. Surfaced so the user knows to reload the plugin.
   */
  skewMessage: string | null;
  /** The signed-in Figma user, from the handshake; the licence follows this id. */
  user: LicenseUser | null;
  /** The free-tier gate as it stands; refreshed whenever the plan or the count changes. */
  gate: Gate;
  /** True when the last Import press was refused by the gate; the licence panel then leads with the reason. */
  gateBlocked: boolean;
  /** Which of our plugins the UI is framed in — the one that answered the handshake. Brands the screens. */
  variant: Variant;
  // User inputs
  file: File | null;
  mode: ImportMode;
  /**
   * Raster scale for High-res image mode (1–4×). 3× ≈ retina/print quality.
   * The engine clamps it down so the longest edge stays ≤ Figma's 4096 px.
   */
  scale: number;
  notice: string | null;
  // Progress / outcome
  status: ImportStatus;
  progress: ImportProgress | null;
  message: string | null;
  result: { count: number; skipped: number } | null;
  // Actions
  /** Ask the sandbox to open a licence link — buying Pro, from the panel. */
  openLicenseLink: (name: LicenseLinkName) => void;
  setFile: (file: File | null) => void;
  setMode: (mode: ImportMode) => void;
  setScale: (scale: number) => void;
  startImport: () => void;
  cancel: () => void;
  reset: () => void;
  /** Re-attempt the plugin handshake after a connection error. */
  retryConnection: () => void;
}

// ============================================
// Plugin bridge
// ============================================

/**
 * The transport, once `plugin-bridge` has loaded. See that module for why it is reached
 * dynamically rather than imported here.
 */
let send: ((message: UIMessage) => void) | null = null;

/**
 * Anything sent before the bridge finished loading.
 *
 * Not theoretical: the store posts `ui-ready` as part of arming the handshake, and a dynamic
 * import takes at least a microtask. Dropping those would hang the UI on "Connecting to
 * Figma…" — the exact failure the relay was built to avoid — so they wait here instead.
 */
const pendingToPlugin: UIMessage[] = [];

function sendToPlugin(message: UIMessage): void {
  if (send === null) {
    pendingToPlugin.push(message);
    return;
  }
  send(message);
}

// ============================================
// Worker singleton
// ============================================

let worker: Worker | null = null;
let jobId = 0;
let pending: { mode: ImportMode; fileName: string } | null = null;
// Artboards that failed to convert in the worker (carried into the final
// success message so a partial import is honest about what was dropped).
let conversionFailed = 0;
// Artboards actually forwarded to the plugin this job (0 ⇒ nothing converted).
let streamedCount = 0;

// Safety net: the plugin sandbox should reply to every 'create-nodes' message
// (import-success or error). If a reply is ever dropped, this keeps the UI from
// hanging on "Placing layers…" forever. Generous so it never pre-empts a large
// but still-progressing placement.
const PLACING_TIMEOUT_MS = 45_000;
let placingWatchdog: ReturnType<typeof setTimeout> | null = null;
// Set when the plugin acknowledges 'create-begin'. Lets the watchdog tell a
// dead UI↔plugin bridge (no ack) apart from a payload the sandbox choked on
// (ack received, but no placement progress).
let beginAcked = false;

// Conversion runs in the MuPDF worker; if the worker dies at load or its WASM
// init throws, it may never post back — leaving the UI stuck on "Converting…".
// Generous because a large multi-page file legitimately takes a while.
const CONVERTING_TIMEOUT_MS = 90_000;
let convertingWatchdog: ReturnType<typeof setTimeout> | null = null;

// The plugin sends 'ready' on startup and in reply to 'ui-ready'. If neither
// arrives, the bridge is dead and the UI would otherwise show "Connecting…"
// forever. We re-ask a few times, then surface a recoverable connection error.
const HANDSHAKE_RETRY_MS = 2_000;
const HANDSHAKE_MAX_RETRIES = 4;
let handshakeWatchdog: ReturnType<typeof setTimeout> | null = null;
let handshakeAttempts = 0;

function clearPlacingWatchdog(): void {
  if (placingWatchdog !== null) {
    clearTimeout(placingWatchdog);
    placingWatchdog = null;
  }
}

function clearConvertingWatchdog(): void {
  if (convertingWatchdog !== null) {
    clearTimeout(convertingWatchdog);
    convertingWatchdog = null;
  }
}

function armConvertingWatchdog(): void {
  clearConvertingWatchdog();
  convertingWatchdog = setTimeout(() => {
    convertingWatchdog = null;
    if (useImportStore.getState().status !== 'converting') return;
    pending = null;
    useImportStore.setState({
      status: 'error',
      progress: null,
      message:
        'Converting timed out — the PDF engine (MuPDF/WASM) didn’t respond. ' +
        'Close and reopen the plugin and try again; if it persists, the file may be too large.',
    });
  }, CONVERTING_TIMEOUT_MS);
}

function clearHandshakeWatchdog(): void {
  if (handshakeWatchdog !== null) {
    clearTimeout(handshakeWatchdog);
    handshakeWatchdog = null;
  }
}

/**
 * Drive the connection handshake: ask the plugin to (re)send 'ready', then wait.
 * Retries a few times to ride out the nested-iframe load, and finally surfaces a
 * recoverable connection error instead of an indefinite spinner.
 */
function armHandshakeWatchdog(): void {
  clearHandshakeWatchdog();
  sendToPlugin({ type: 'ui-ready', protocolVersion: PROTOCOL_VERSION });
  handshakeWatchdog = setTimeout(() => {
    handshakeWatchdog = null;
    if (useImportStore.getState().isReady) return;
    if (handshakeAttempts < HANDSHAKE_MAX_RETRIES) {
      handshakeAttempts += 1;
      armHandshakeWatchdog();
      return;
    }
    /*
     * NEITHER SIDE IS NAMED AS THE CULPRIT, and the previous wording got that wrong.
     *
     * It said "reload/re-import the plugin (its sandbox code may need updating)", which reads
     * as "your code.js is stale". The time this actually fired, the sandbox was the correct
     * half: the plugin id had changed, the sandbox was rebuilt with it, and this UI had not
     * deployed yet — so the advice pointed at the only part that was up to date. Whoever
     * followed it would re-import a correct plugin and see the same message again.
     *
     * The two halves version independently and either can be behind, so the message now says
     * that rather than guessing.
     */
    useImportStore.setState({
      connectionError:
        'Couldn’t reach the Figma plugin sandbox — the UI↔plugin bridge isn’t connected. ' +
        'Close and reopen the plugin. If it persists, the plugin and this screen are out of ' +
        'step with each other: re-import the latest plugin, and if that does not help the ' +
        'hosted screen is still catching up — try again shortly.',
    });
  }, HANDSHAKE_RETRY_MS);
}

function armPlacingWatchdog(): void {
  clearPlacingWatchdog();
  placingWatchdog = setTimeout(() => {
    placingWatchdog = null;
    const { status, progress } = useImportStore.getState();
    if (status !== 'placing') return;
    pending = null;
    // Three distinct failures, told apart by the begin-ack + progress count:
    //   • no ack          → the import request never round-tripped to the
    //                        plugin: a UI↔plugin bridge problem, not the file.
    //   • ack, 0 progress → the request arrived but the first artboard's
    //                        payload choked the sandbox (too large/complex).
    //   • ack, k progress → it placed k, then stalled partway.
    let message: string;
    if (!beginAcked) {
      message =
        'The plugin isn’t receiving the import request — this looks like a Figma UI↔plugin connection problem, not your file. Close and reopen the plugin, then try again.';
    } else if (!progress || progress.completed === 0) {
      message =
        'Figma received the request but couldn’t place the first artboard — it’s likely too large or complex. Try High-res image mode, or a simpler file.';
    } else {
      message = `Figma stopped responding after placing ${progress.completed} of ${progress.total} artboards. Please try again.`;
    }
    useImportStore.setState({ status: 'error', progress: null, message });
  }, PLACING_TIMEOUT_MS);
}

function failConversion(message: string): void {
  clearConvertingWatchdog();
  pending = null;
  if (useImportStore.getState().status === 'converting') {
    useImportStore.setState({ status: 'error', progress: null, message });
  }
}

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('../lib/image.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (event: MessageEvent<WorkerResponse>) =>
      handleWorkerMessage(event.data);
    // Without these, a worker that dies at load or whose WASM init throws fails
    // silently — the UI would sit on "Converting…" with no error. Surface it.
    worker.onerror = (event: ErrorEvent) => {
      failConversion(
        `The image decoder failed to load: ${event.message || 'worker error'}. ` +
          'Close and reopen the plugin and try again.',
      );
    };
    worker.onmessageerror = () => {
      failConversion('The image decoder sent an unreadable response. Please try again.');
    };
  }
  return worker;
}

function postToWorker(
  request: WorkerRequest,
  transfer: Transferable[] = [],
): void {
  getWorker().postMessage(request, transfer);
}

// ============================================
// Store
// ============================================

export const useImportStore = create<ImportState>((set, get) => ({
  isReady: false,
  editorType: null,
  connectionError: null,
  skewMessage: null,
  user: null,
  gate: licensing.gate(),
  gateBlocked: false,
  variant: DEFAULT_VARIANT,
  file: null,
  // Default to image: it's the documented-reliable path (createImage). The
  // 'vector' path uses Figma's synchronous createNodeFromSvg, which can freeze
  // the sandbox on complex/text-heavy pages, so it's opt-in.
  mode: 'image',
  // 3× ≈ retina/print quality — the documented default for image mode.
  scale: 3,
  notice: null,
  status: 'idle',
  progress: null,
  message: null,
  result: null,

  openLicenseLink: (name) => sendToPlugin({ type: 'open-external', link: name }),

  setFile: (file) => {
    if (!file) {
      set({ file: null, notice: null });
      return;
    }
    const error = validateFile(file);
    if (error) {
      set({ file: null, notice: error });
      return;
    }
    set({
      file,
      notice: null,
      status: 'idle',
      progress: null,
      message: null,
      result: null,
    });
    // Warm up the worker's WASM while the user picks a mode.
    postToWorker({ type: 'warmup' });
  },

  setMode: (mode) => set({ mode }),

  setScale: (scale) => set({ scale }),

  startImport: () => {
    const { file, mode, status } = get();
    if (!file || status === 'converting' || status === 'placing') return;

    // The monthly allowance, shared across every tomake.design plugin, Refusing here, before any work,
    // is what lets the licence panel explain and offer the fix in place.
    const gate = licensing.gate({ kind: kindOf(mode) });
    if (!gate.allowed) {
      set({ gate, gateBlocked: true });
      return;
    }
    set({ gateBlocked: false });

    const id = (jobId += 1);
    pending = { mode, fileName: file.name };
    set({
      status: 'converting',
      progress: { completed: 0, total: 0 },
      message: 'Decoding your image…',
      result: null,
    });

    // The file never leaves the browser: its bytes go straight to the ImageMagick worker.
    file
      .arrayBuffer()
      .then((data) => {
        if (jobId !== id) return; // superseded or cancelled
        armConvertingWatchdog();
        set({ message: null });
        postToWorker({ type: 'convert', id, data, name: file.name }, [data]);
      })
      .catch((error: unknown) => {
        clearConvertingWatchdog();
        if (jobId !== id) return;
        pending = null;
        set({
          status: 'error',
          progress: null,
          message: error instanceof Error ? error.message : 'Could not read this file.',
        });
      });
  },

  cancel: () => {
    clearPlacingWatchdog();
    clearConvertingWatchdog();
    postToWorker({ type: 'cancel', id: jobId });
  },

  reset: () => {
    clearPlacingWatchdog();
    clearConvertingWatchdog();
    set({
      status: 'idle',
      progress: null,
      message: null,
      result: null,
      notice: null,
    });
  },

  retryConnection: () => {
    handshakeAttempts = 0;
    set({ connectionError: null });
    armHandshakeWatchdog();
  },
}));

// ============================================
// Message handlers (update the store from outside React)
// ============================================

function handleWorkerMessage(message: WorkerResponse): void {
  if (message.id !== jobId) return; // ignore superseded jobs

  switch (message.type) {
    case 'begin': {
      // Conversion has started and we know the count — open the placement stream
      // to the plugin now, so artboards are placed as they convert (pipelined)
      // rather than after the whole file is done.
      clearConvertingWatchdog();
      if (!pending) return;

      /*
       * THE PAGE LIMIT, applied here because this is the only honest moment for it: before
       * this message the page count is unknown, and after it artboards are already going into
       * the document. Refusing half-placed work would be worse than not refusing at all.
       *
       * The worker is told to stop so the rest of the file is not converted for nothing, and
       * `jobId` moves on so anything already in flight is ignored.
       */
      const pageGate = licensing.gate({ kind: kindOf(pending.mode), pages: message.total });
      if (!pageGate.allowed) {
        postToWorker({ type: 'cancel', id: jobId });
        pending = null;
        jobId += 1;
        useImportStore.setState({
          status: 'idle',
          progress: null,
          message: null,
          gate: pageGate,
          gateBlocked: true,
        });
        return;
      }

      beginAcked = false;
      conversionFailed = 0;
      streamedCount = 0;
      try {
        sendToPlugin({
          type: 'create-begin',
          mode: pending.mode,
          fileName: pending.fileName,
          total: message.total,
        });
      } catch (error) {
        failPlacement(error);
        return;
      }
      useImportStore.setState({
        status: 'placing',
        progress: { completed: 0, total: message.total },
      });
      armPlacingWatchdog();
      break;
    }

    case 'artboard':
      if (!pending) return;
      // Forward each converted artboard to the plugin immediately. Re-arm the
      // placing watchdog: the worker producing an artboard proves the pipeline
      // is alive even if the plugin hasn't reported placement progress yet.
      armPlacingWatchdog();
      try {
        sendToPlugin({
          type: 'place-artboard',
          index: message.index,
          artboard: message.artboard,
        });
        streamedCount += 1;
      } catch (error) {
        failPlacement(error);
      }
      break;

    case 'done':
      if (!pending) return;
      conversionFailed = message.failed;
      // Nothing converted (every artboard failed) — report it directly rather
      // than asking the plugin to finalise an empty stream. The plugin's open
      // session is harmlessly replaced on the next import.
      if (streamedCount === 0) {
        clearPlacingWatchdog();
        pending = null;
          useImportStore.setState({
          status: 'error',
          progress: null,
          message:
            message.failed > 0
              ? `None of the ${message.failed} artboard${message.failed === 1 ? '' : 's'} could be converted. The file may be malformed or unsupported.`
              : 'No artboards were found in this file.',
        });
        break;
      }
      // All artboards converted+streamed. Close the placement stream; the plugin
      // finalises and replies with import-success (or error).
      try {
        sendToPlugin({ type: 'create-end' });
      } catch (error) {
        failPlacement(error);
      }
      break;

    case 'cancelled':
      // Stop. Any artboards already placed remain on the canvas (the plugin's
      // open session is harmlessly replaced on the next import); we don't try to
      // un-place them. Late 'artboard' messages are ignored via the null pending.
      clearConvertingWatchdog();
      clearPlacingWatchdog();
      pending = null;
      useImportStore.setState({ status: 'idle', progress: null, message: null });
      break;

    case 'error':
      clearConvertingWatchdog();
      clearPlacingWatchdog();
      pending = null;
      useImportStore.setState({
        status: 'error',
        progress: null,
        message: message.message,
      });
      break;
  }
}

/** Report a failure that occurred while streaming artboards to the plugin. */
function failPlacement(error: unknown): void {
  clearPlacingWatchdog();
  pending = null;
  useImportStore.setState({
    status: 'error',
    progress: null,
    message:
      error instanceof Error
        ? error.message
        : 'Could not hand the artboards to Figma.',
  });
}

function handlePluginMessage(message: PluginMessage): void {
  switch (message.type) {
    case 'ready': {
      // Only accept a handshake from one of OUR plugins. This deploy serves
      // several Community listings (see variants.json); the id says which one
      // framed us, and it drives the title, the blurb and the accepted
      // extensions. A 'ready' carrying a different (or missing) id isn't ours —
      // ignore it and keep showing "Connecting to Figma…".
      const variant = variantById(message.pluginId);
      if (!variant) break;
      clearHandshakeWatchdog();
      // The handshake completed, so the bridge is alive. But if the sandbox
      // reports a different (or, for a pre-v3 code.js, missing) protocol version,
      // the locally-loaded plugin is out of date relative to this auto-deployed
      // UI — every import would silently no-op. Surface it instead of pretending
      // we're ready.
      if (message.protocolVersion !== PROTOCOL_VERSION) {
        useImportStore.setState({
          isReady: false,
          editorType: message.editorType,
          connectionError: null,
          variant,
          skewMessage:
            'This plugin’s code is out of date and can’t talk to the current ' +
            'importer. In Figma: Plugins → Development → right-click ' +
            `“${variant.name}” → Reload (or re-import the manifest), then ` +
            'reopen the plugin.',
        });
        break;
      }
      const user = message.user ?? null;
      // Ask the licence server about this Figma user: a licence bought in any
      // tomake.design plugin shows up here without a key being typed again.
      void licensing.sync(user).then(() => useImportStore.setState({ gate: licensing.gate() }));
      useImportStore.setState({
        isReady: true,
        editorType: message.editorType,
        connectionError: null,
        skewMessage: null,
        user,
        variant,
      });
      break;
    }

    case 'begin-ack':
      // Ignore late messages from a cancelled/superseded job — `pending` is
      // cleared on cancel/done, so a drained plugin backlog can't resurrect the
      // placing state (and a false "stopped responding" watchdog error).
      if (!pending) return;
      // The request reached the sandbox and the reply path works — keep the
      // watchdog alive while the plugin starts on the first artboard.
      beginAcked = true;
      armPlacingWatchdog();
      break;

    case 'place-progress':
      if (!pending) return;
      // The plugin is alive and placing — refresh progress and reset the
      // watchdog so it only fires on a genuine stall, not slow-but-progressing.
      armPlacingWatchdog();
      useImportStore.setState({
        status: 'placing',
        progress: {
          completed: message.placed + message.skipped,
          total: message.total,
        },
      });
      break;

    case 'import-success': {
      if (!pending) return;
      clearPlacingWatchdog();
      licensing.recordImport(useImportStore.getState().user, Math.max(1, message.count), kindOf(pending.mode));
      pending = null;
      const rasterized = message.rasterized ?? 0;
      const textLayers = message.textLayers ?? 0;
      const failed = conversionFailed;
      conversionFailed = 0;
      useImportStore.setState({
        status: 'done',
        progress: null,
        result: { count: message.count, skipped: message.skipped },
        message:
          `Imported ${message.count} artboard${message.count === 1 ? '' : 's'}` +
          (message.skipped > 0 ? ` · ${message.skipped} skipped` : '') +
          (textLayers > 0 ? ` · ${textLayers} editable text layers` : '') +
          (rasterized > 0
            ? ` · ${rasterized} too complex for editable layers, imported as image`
            : '') +
          (failed > 0
            ? ` · ${failed} couldn’t be converted`
            : ''),
      });
      break;
    }

    case 'error':
      clearPlacingWatchdog();
      pending = null;
      useImportStore.setState({
        status: 'error',
        progress: null,
        message: message.message,
      });
      break;
  }
}

// ============================================
// One-time plugin handshake (runs when this module loads)
// ============================================

if (typeof window !== 'undefined') {
  /*
   * Load the transport and connect it. Dynamic because `@create-figma-plugin/utilities`
   * inspects `window` at module scope and throws under Node — see `plugin-bridge.ts`.
   */
  void import('./plugin-bridge').then((bridge) => {
    bridge.connect(handlePluginMessage);
    send = bridge.send;
    // Anything the store tried to send while this was loading, in the order it was sent.
    while (pendingToPlugin.length > 0) {
      const message = pendingToPlugin.shift();
      if (message) bridge.send(message);
    }
  });
  // Ask the plugin to (re)send its ready state, avoiding a startup race, and arm
  // the handshake watchdog so a dead bridge surfaces an error rather than an
  // indefinite "Connecting to Figma…" spinner.
  armHandshakeWatchdog();
  // Eagerly warm the MuPDF/WASM engine (~10 MB) the moment the UI loads, so it's
  // ready before the user picks a file and clicks Import — the first conversion
  // then starts immediately instead of waiting on a cold WASM load.
  postToWorker({ type: 'warmup' });
}

// Compile-time guarantee: this store satisfies the shared ImporterFlow contract.
conformsToImporterFlow(useImportStore);

licensing.subscribe(() => useImportStore.setState({ gate: licensing.gate() }));
